import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { Users, FileText, CheckCircle2, PoundSterling, TrendingUp, ArrowRight } from "lucide-react";

const STAT_ICONS = {
    total_leads: Users,
    new_leads: FileText,
    quoted: FileText,
    booked: CheckCircle2,
    conversion_rate: TrendingUp,
    estimated_booking_value: PoundSterling,
};

function StatCard({ label, value, icon: Icon, suffix }) {
    return (
        <div data-testid={ADMIN.statCard} className="rounded-xl bg-white border border-[#17252D]/10 p-5">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-[#17252D]/60">{label}</div>
                {Icon && <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#001B3D]/10 text-[#001B3D]"><Icon className="h-4 w-4" /></span>}
            </div>
            <div data-testid={ADMIN.statValue} className="mt-2 text-3xl font-heading font-semibold">
                {value}
                {suffix && <span className="text-lg text-[#17252D]/60 ml-1">{suffix}</span>}
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Failed to load stats"));
        api.get("/admin/leads", { params: { limit: 6 } }).then((r) => setRecent(r.data.items || []));
    }, []);

    if (err) return <div className="rounded-xl bg-white border p-4 text-sm text-red-600">{err}</div>;
    if (!stats) return <div className="text-sm text-[#17252D]/60">Loading dashboard…</div>;

    const pieData = (stats.by_service || []).slice(0, 6);
    const pieColours = ["#001B3D", "#03283D", "#5A8AA2", "#7BC7C4", "#B8D8DC", "#14A6A1"];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-heading font-semibold">Dashboard</h1>
                <p className="text-sm text-[#17252D]/60">Overview of your enquiries and pipeline.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Total leads" value={stats.total_leads} icon={STAT_ICONS.total_leads} />
                <StatCard label="New" value={stats.new_leads} icon={STAT_ICONS.new_leads} />
                <StatCard label="Quoted" value={stats.quoted} icon={STAT_ICONS.quoted} />
                <StatCard label="Booked" value={stats.booked} icon={STAT_ICONS.booked} />
                <StatCard label="Completed" value={stats.completed} icon={STAT_ICONS.booked} />
                <StatCard label="Lost" value={stats.lost} icon={FileText} />
                <StatCard label="Conversion" value={stats.conversion_rate} suffix="%" icon={STAT_ICONS.conversion_rate} />
                <StatCard label="Est. booking value" value={`£${Number(stats.estimated_booking_value).toLocaleString("en-GB")}`} icon={STAT_ICONS.estimated_booking_value} />
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
                <div className="rounded-xl bg-white border border-[#17252D]/10 p-4 lg:col-span-2">
                    <div className="text-sm font-semibold mb-2">Enquiries — last 7 days</div>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={stats.last_7_days}>
                                <defs>
                                    <linearGradient id="gAccent" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#001B3D" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="#001B3D" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="date" tickFormatter={(d) => (d ? d.slice(5) : "")} stroke="#17252D80" fontSize={12} />
                                <YAxis allowDecimals={false} stroke="#17252D80" fontSize={12} />
                                <Tooltip />
                                <Area type="monotone" dataKey="count" stroke="#001B3D" fill="url(#gAccent)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="rounded-xl bg-white border border-[#17252D]/10 p-4">
                    <div className="text-sm font-semibold mb-2">By service</div>
                    <div className="h-64">
                        {pieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={pieData} dataKey="count" nameKey="service" innerRadius={40} outerRadius={80}>
                                        {pieData.map((_, i) => <Cell key={i} fill={pieColours[i % pieColours.length]} />)}
                                    </Pie>
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex items-center justify-center text-sm text-[#17252D]/60">No leads yet — submit a test quote.</div>
                        )}
                    </div>
                </div>
            </div>
            <div className="rounded-xl bg-white border border-[#17252D]/10">
                <div className="p-4 border-b border-[#17252D]/10 flex items-center justify-between">
                    <div className="font-semibold">Recent leads</div>
                    <Link to="/admin/leads" className="text-sm text-[#001B3D] inline-flex items-center gap-1">All leads <ArrowRight className="h-4 w-4" /></Link>
                </div>
                <ul className="divide-y divide-[#17252D]/10">
                    {recent.length === 0 && <li className="p-4 text-sm text-[#17252D]/60">No leads yet.</li>}
                    {recent.map((l) => (
                        <li key={l.id} className="p-4 hover:bg-[#F3F7F9]">
                            <Link to={`/admin/leads/${l.id}`} className="flex items-center gap-3">
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#001B3D]/10 text-[#001B3D] text-xs font-mono">{l.reference?.slice(-4)}</span>
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium truncate">{l.full_name} — {l.service}</div>
                                    <div className="text-xs text-[#17252D]/60 truncate">{l.collection_postcode || "—"} → {l.delivery_postcode || "—"} • {(l.created_at || "").slice(0, 10)}</div>
                                </div>
                                <span className="text-xs px-2 py-1 rounded-full border border-[#001B3D]/25 text-[#001B3D]">{l.status}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
